/**
 * Copyright (C) 2026 Guido Berhoerster <guido+voxit@berhoerster.name>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import * as voxitAdminWebClient from "./voxit-admin-web-client.js";

export class VoxitStatementEditor {
    constructor() {
        this.dialog = document.querySelector("#statement-editor");
        this.dialog.addEventListener("submit", this);

        this.formElement = document.forms["statement-form"];
    }

    open(conversationID, statement) {
        if (statement) {
            this.dialog.querySelector("#create-statement-title").hidden = true;
            this.dialog.querySelector("#edit-statement-title").hidden = false;
        } else {
            this.dialog.querySelector("#create-statement-title").hidden = false;
            this.dialog.querySelector("#edit-statement-title").hidden = true;
            statement = new voxitAdminWebClient.Statement()
        }
        this.formElement.querySelector("#statement-message").hidden = true;
        this.formElement["conversation_id"].value = conversationID;
        this.formElement["statement_id"].value = statement.id;
        this.formElement["moderation_status"].value = statement.moderationStatus;
        this.formElement["text"].value = statement.text;
        // order matters, meta statements are usually also seed statements
        this.formElement["type"].value = "regular";
        if (statement.isMeta) {
            this.formElement["type"].value = "meta";
        } else if (statement.isSeed) {
            this.formElement["type"].value = "seed";
        } else {
            this.formElement["type"].value = "regular";
        }

        this.dialog.showModal();
    }

    close() {
        this.dialog.close();
    }

    showStatementExistsError() {
        this.formElement.querySelector("#statement-message").hidden = false;
    }

    onSubmit() {
        const conversationID = this.formElement["conversation_id"].value;
        const statement = new voxitAdminWebClient.Statement({
            "id": Number(this.formElement["statement_id"].value),
            "text": this.formElement["text"].value.trim(),
            "is_meta": this.formElement["type"].value === "meta",
            // meta statements are usually also seed statements
            "is_seed": ["meta", "seed"].includes(this.formElement["type"].value),
            "moderation_status": this.formElement["moderation_status"].value,
        });
        const event = new CustomEvent("submitstatement", {
            detail: {
                conversationID,
                statement,
            }
        });
        this.dialog.dispatchEvent(event);
    }

    handleEvent(event) {
        console.debug(event)
        switch (event.type) {
        case "submit":
            event.preventDefault();

            this.onSubmit();
            break;
        }
    }
}

export class VoxitAdminFrontend {
    #loginWindow = null;
    #loginWindowTimer = null;
    #errorDialog;
    #loginDialog;

    constructor(client) {
        this.client = client;

        this.#errorDialog = document.querySelector("#error-dialog");
        this.#loginDialog = document.querySelector("#login-dialog");
        this.#loginDialog.addEventListener("command", this);

        document.querySelector("#admin-frontend").addEventListener("command", this);

        window.addEventListener("message", this);
    }

    #openAuthenticationPopup(url, name) {
        const width = 600;
        const height = 600;
        const left = Math.max(window.screen.width - width, 0) / 2;
        const top = Math.max(window.screen.height - height, 0) / 2;
        return window.open(
            url, name, `popup width=${width} height=${height} top=${top} left=${left}`,
        );
    }

    #onLoginMessage() {
        clearInterval(this.#loginWindowTimer ?? undefined);
        this.#loginWindowTimer = null;
        this.#loginWindow.close();
        this.#loginWindow = null;

        this.#loginDialog.close();
        for (const buttonElement of this.#loginDialog.querySelectorAll("button[command='--login']")) {
            buttonElement.disabled = false;
        }

        this.onLogin();
    }

    #onLoginWindowTimer() {
        if (this.#loginWindow === null || this.#loginWindow.closed) {
            clearInterval(this.#loginWindowTimer ?? undefined);

            this.#loginWindowTimer = null;
            this.#loginWindow = null;

            for (const buttonElement of this.#loginDialog.querySelectorAll("button[command='--login']")) {
                buttonElement.disabled = false;
            }
        }
    }

    onLogin() {}

    startAuthentication() {
        if (this.#loginWindow !== null) {
            return;
        }

        this.#loginWindow = this.#openAuthenticationPopup(this.client.loginURL, "voxit-admin-login");
        if (this.#loginWindow === null) {
            return;
        }

        for (const buttonElement of this.#loginDialog.querySelectorAll("button[command='--login']")) {
            buttonElement.disabled = true;
        }

        // check if window has been closed without successful authentication
        this.#loginWindowTimer = setInterval(
            this.#onLoginWindowTimer.bind(this),
            1000,
        );
    }

    async logout() {
        let oidcLogoutURL;
        try {
            oidcLogoutURL = await this.client.logout();
        } catch (error) {
            this.handleClientError(error);
            console.error("Failed to log out:", error);
            return;
        }

        if (oidcLogoutURL !== "") {
            this.#openAuthenticationPopup(oidcLogoutURL, "voxit-admin-logout");
        }

        this.#loginDialog.showModal();
    }

    handleClientError(error) {
        if (error instanceof voxitAdminWebClient.AuthenticationRequiredError) {
            this.#loginDialog.showModal();
            return;
        } else if (error instanceof voxitAdminWebClient.BaseError) {
            this.#errorDialog.querySelector("#error-title").textContent = error.message;
            this.#errorDialog.querySelector("#error-details").textContent = error.detail ?? "";
            this.#errorDialog.showModal();
            return;
        }
        throw(error);
    }

    handleEvent(event) {
        console.debug(event);
        switch (event.type) {
        case "message":
            if (event.origin !== this.client.baseURL.origin) {
                console.error(`message origin ${event.origin} does not match ${this.client.baseURL.origin}`);
                return;
            }
            if (typeof event.data === "string" && event.data !== "") {
                console.error(`unexpected content: ${event.data}`);
                return;
            }
            this.#onLoginMessage();
            break;
        case "command":
            if (event.command === "--logout") {
                this.logout();
            } else if (event.command === "--login") {
                this.startAuthentication();
            }
            break;
        }
    }
}
